import { getCategoryBadgeStyle } from '@/features/campaigns/EmailCampaign/templates/config';
import { useTranslation } from 'react-i18next';
import { prepareEmailBodyHtml } from './emailTemplateHtml';
import './emailTemplatePreview.scss';
import { replaceEmailTemplateVariables } from './replaceEmailTemplateVariables';

export type EmailTemplateFormData = {
  template_name?: string;
  template_category?: string;
  template_subject?: string;
  template_body_html?: string;
  template_body_text?: string;
  template_id?: string;
};

type EmailTemplatePreviewProps = {
  formData: EmailTemplateFormData;
};

export const EmailTemplatePreview = ({ formData }: EmailTemplatePreviewProps) => {
  const { t } = useTranslation('campaign');
  const { t: tCommon } = useTranslation('COMMON');

  const category = formData.template_category?.trim() || tCommon('notSelectedText');
  const templateName = formData.template_name?.trim() || tCommon('notSelectedText');
  const subject = replaceEmailTemplateVariables(formData.template_subject) || t('emailWorkspace.templates.emailPreviewSubjectPlaceholder');
  const sanitizedBodyHtml = prepareEmailBodyHtml(formData.template_body_html);
  const bodyText = replaceEmailTemplateVariables(formData.template_body_text?.trim());
  const footerText = replaceEmailTemplateVariables(t('emailWorkspace.templates.emailPreviewFooter'));

  const hasBodyHtml = sanitizedBodyHtml.length > 0;
  const hasBodyText = bodyText.length > 0;
  const hasAnyBody = hasBodyHtml || hasBodyText;

  return (
    <aside className='sticky top-6 border border-black/50 p-4 rounded-lg'>
      <div className='mb-3 flex items-center justify-between gap-3'>
        <div>
          <h2 className='text-xl font-semibold text-gray-900'>{t('emailWorkspace.templates.emailPreview')}</h2>
          <p className='mt-1 text-xs text-gray-500'>{t('emailWorkspace.templates.emailPreviewDescription')}</p>
        </div>

        <span className={`shrink-0 rounded-full px-3 py-1 text-xs font-medium uppercase tracking-[0.08em] ${getCategoryBadgeStyle(category)}`}>{category}</span>
      </div>

      <div className='my-4 grid grid-cols-2 gap-2 text-xs'>
        <div className='rounded-xl border border-gray-200 bg-white p-3'>
          <div className='text-gray-400'>{t('emailWorkspace.templates.emailPreviewMetaName')}</div>
          <div className='mt-1 font-medium text-gray-800'>{templateName}</div>
        </div>

        <div className='rounded-xl border border-gray-200 bg-white p-3'>
          <div className='text-gray-400'>{t('emailWorkspace.templates.emailPreviewMetaCategory')}</div>
          <div className='mt-1 font-medium text-gray-800'>{category}</div>
        </div>
      </div>

      <div className='mx-auto w-full max-w-[390px] overflow-hidden rounded-2xl border border-secondary-border bg-white shadow-sm'>
        <div className='space-y-4 px-5 py-6'>
          <div>
            <p className='text-[10px] font-semibold uppercase tracking-[0.12em] text-quaternary-500'>
              {t('emailWorkspace.templates.emailPreviewSubjectLabel')}
            </p>
            <h3 className='mt-1 text-xl font-bold leading-tight text-slate-900'>{subject}</h3>
          </div>

          <div className='space-y-4 text-secondary-600'>
            {hasBodyHtml ? (
              <div
                className='email-template-preview__body prose prose-sm max-w-none text-secondary-600'
                dangerouslySetInnerHTML={{ __html: sanitizedBodyHtml }}
              />
            ) : null}

            {hasBodyText ? (
              <div className={hasBodyHtml ? 'border-t border-secondary-border pt-4' : ''}>
                {hasBodyHtml ? (
                  <p className='mb-2 text-[10px] font-semibold uppercase tracking-[0.12em] text-quaternary-500'>
                    {t('emailWorkspace.templates.emailPreviewPlainTextLabel')}
                  </p>
                ) : null}
                <p className='whitespace-pre-wrap text-sm leading-6 text-secondary-600'>{bodyText}</p>
              </div>
            ) : null}

            {!hasAnyBody ? <p className='text-sm leading-6 text-gray-400'>{t('emailWorkspace.templates.emailPreviewBodyPlaceholder')}</p> : null}
          </div>

          <div className='space-y-1 border-t border-secondary-border pt-4 text-center text-[11px] leading-5 text-quaternary-500'>
            {footerText.split('\n').map((line) => (
              <p key={line}>{line}</p>
            ))}
          </div>
        </div>
      </div>
    </aside>
  );
};
